import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiX, FiMapPin, FiClock, FiSearch } from "react-icons/fi";
import { API_KEY } from "../Constant/TextConstant";
import LocationDisplay from "./LocationDisplay";
import "./LocationSearchModel.css";

const LocationSearchModal = ({
  showModal,
  setShowModal,
  setUserLocation,
  currentLocation,
  isLoggedIn,
  navigate,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentLocationLoading, setCurrentLocationLoading] = useState(false);
  const searchRef = useRef(null);
  const modalRef = useRef(null);

  // Load recent searches from sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem("recentLocationSearches");
    const savedSearches = saved ? JSON.parse(saved) : [];
    setRecentSearches(savedSearches);
  }, []);

  // Save to recent searches in sessionStorage
  const saveRecentSearch = useCallback(
    (location) => {
      const updatedSearches = [
        location,
        ...recentSearches.filter((item) => item.address !== location.address),
      ].slice(0, 5);

      setRecentSearches(updatedSearches);
      sessionStorage.setItem(
        "recentLocationSearches",
        JSON.stringify(updatedSearches)
      );
    },
    [recentSearches]
  );

  // Get more details about a location from coordinates
  const getLocationDetails = useCallback(async (lat, lng) => {
    try {
      const response = await fetch(
        `https://api.olamaps.io/places/v1/reverse-geocode?latlng=${lat},${lng}&api_key=${API_KEY.OLA_API_KEY}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.results?.length) {
          const result = data.results[0];
          const components = result.address_components || [];

          let streetName = "";
          let subLocality = "";
          let locality = "";
          let pincode = "";

          components.forEach((component) => {
            const types = component.types || [];
            if (types.includes("street_address")) {
              streetName = component.long_name;
            } else if (
              types.includes("sublocality") ||
              types.includes("administrative_area_level_3")
            ) {
              subLocality = component.long_name;
            } else if (types.includes("locality")) {
              locality = component.long_name;
            } else if (types.includes("postal_code")) {
              pincode = component.long_name;
            }
          });

          return {
            streetName,
            subLocality,
            locality,
            pincode,
            formattedAddress: result.formatted_address,
          };
        }
      }
      return null;
    } catch (err) {
      return null;
    }
  }, []);

  // Convert address to coordinates
  const fetchCoordinates = useCallback(
    async (address) => {
      setIsLoading(true);
      setError("");

      try {
        const response = await fetch(
          `https://api.olamaps.io/places/v1/geocode?address=${encodeURIComponent(
            address
          )}&language=English&api_key=${API_KEY.OLA_API_KEY}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.geocodingResults?.length) {
            const location = data.geocodingResults[0];
            const { lat, lng } = location.geometry.location;

            const locationDetails = await getLocationDetails(lat, lng);

            return {
              coordinates: { latitude: lat, longitude: lng },
              address: location.formatted_address || address,
              details: locationDetails,
            };
          } else {
            throw new Error("Location not found");
          }
        } else {
          throw new Error("Failed to fetch coordinates");
        }
      } catch (err) {
        setError("Could not find this location. Please try a different one.");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [getLocationDetails]
  );

  // Fetch location suggestions
  const fetchLocationSuggestions = useCallback(async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const url = `https://api.olamaps.io/places/v1/autocomplete?input=${encodeURIComponent(
        query
      )}&api_key=${API_KEY.OLA_API_KEY}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const predictions = data.predictions || [];
        setSuggestions(predictions.map((p) => p.description));
      } else {
        throw new Error(`Failed to fetch suggestions: ${response.status}`);
      }
    } catch (err) {
      setError("Could not load location suggestions. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        fetchLocationSuggestions(searchQuery);
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, fetchLocationSuggestions]);

  const handleLocationSelect = useCallback(
    async (location) => {
      setSearchQuery(location);
      setSuggestions([]);

      const locationData = await fetchCoordinates(location);
      if (locationData) {
        saveRecentSearch(locationData);
        setUserLocation(locationData);
        setShowModal(false);
      }
    },
    [fetchCoordinates, saveRecentSearch, setUserLocation, setShowModal]
  );

  const handleGetCurrentLocation = useCallback(
    async (e) => {
      if (e) e.preventDefault();

      if (!navigator.geolocation) {
        setError("Geolocation not supported");
        return;
      }

      setCurrentLocationLoading(true);
      setError("");

      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        });

        const { latitude, longitude } = position.coords;
        const details = await getLocationDetails(latitude, longitude);

        const locationData = {
          coordinates: { latitude, longitude },
          address: details?.formattedAddress || "Current Location",
          details,
        };

        saveRecentSearch(locationData);
        setUserLocation(locationData);
        setShowModal(false);
      } catch (err) {
        setError("Failed to get current location");
      } finally {
        setCurrentLocationLoading(false);
      }
    },
    [getLocationDetails, saveRecentSearch, setUserLocation, setShowModal]
  );

  return (
    <div className={`modal-overlay ${showModal ? "visible" : "hidden"}`}>
      <div ref={modalRef} className="modal-container">
        <div className="modal-header">
          <h1>Choose your location</h1>
          <button onClick={() => setShowModal(false)} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="modal-body">
          <p>Select a location to see product availability</p>

          {/* Current Location Preview */}
          {currentLocation && (
            <div className="current-location-preview">
              <LocationDisplay
                location={currentLocation}
                onClick={() => handleLocationSelect(currentLocation.address)}
                showFullAddress
              />
            </div>
          )}

          {/* Search Input */}
          <div className="search-container">
            <FiSearch className="search-icon" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Enter area or landmark"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {isLoading && <div className="loading-spinner"></div>}
          </div>

          {/* Error Message */}
          {error && <div className="error-message">{error}</div>}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="suggestions-container">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  onClick={() => handleLocationSelect(suggestion)}
                  className="suggestion-item"
                >
                  <FiMapPin className="suggestion-icon" />
                  <span className="suggestion-text">{suggestion}</span>
                </div>
              ))}
            </div>
          )}

          {/* Current Location Button */}
          <button
            onClick={handleGetCurrentLocation}
            disabled={currentLocationLoading}
            className="current-location-btn"
          >
            <FiMapPin />
            {currentLocationLoading ? "Detecting..." : "Use current location"}
          </button>

          {/* Recent Searches */}
          {recentSearches.length > 0 && (
            <div className="recent-searches-container">
              <h3 className="recent-searches-title">Recent locations</h3>
              <ul className="recent-searches-list">
                {recentSearches.map((search, index) => (
                  <li
                    key={index}
                    onClick={() => handleLocationSelect(search.address)}
                    className="recent-search-item"
                  >
                    <FiClock className="recent-icon" />
                    <span className="recent-text">{search.address}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(LocationSearchModal);
